import { useUserContext } from "../stores/userContext";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import axios from "axios";
import type { AxiosInstance } from "axios";


export const useHttpClient = (needToken = true): AxiosInstance => {
  const { user, setUser } = useUserContext();
  const navigate = useNavigate();
  const apiRef = useRef<AxiosInstance | null>(null);
  const accessTokenRef = useRef<string>("");

  if (!apiRef.current) {
    const baseURL = import.meta.env.VITE_API_URL;
    const api = axios.create({ baseURL });

    // attach interceptors only once
    api.interceptors.request.use(
      (config: any) => {
        if (needToken) {
          const token = accessTokenRef.current || user?.accessToken;
          if (token) {
            config.headers = {
              ...config.headers,
              Authorization: token,
            };
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const response = error.response || {};

        if (!originalRequest.retryCount) {
          originalRequest.retryCount = 0;
        }

        if (needToken && response?.status === 401 && originalRequest.retryCount < 3) {
          originalRequest.retryCount += 1;
          try {
            if (!user?.refreshToken) throw new Error("No refresh token");

            const refreshResponse = await axios.post(
              `${baseURL}${import.meta.env.VITE_REFRESH_TOKEN}`,
              {},
              {
                headers: {
                  Authorization: user.refreshToken,
                  "Content-Type": "application/json",
                },
              }
            );

            const newAccessToken = refreshResponse.data.access_token;
            if (!newAccessToken) {
              navigate("/signinemail");
            } else {
              setUser({ ...user, accessToken: newAccessToken });
              accessTokenRef.current = newAccessToken;
            }

            originalRequest.headers.Authorization = accessTokenRef.current;
            return api(originalRequest);
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            navigate("/signinemail");
          }
        } else if (originalRequest.retryCount === 3) {
          navigate("/signinemail");
        }

        return Promise.reject(error);
      }
    );

    apiRef.current = api;
  }

  return apiRef.current;
};
